import { RouteOutlined, EvStationOutlined, Close, CompareArrows } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, TextField, Typography, Button, IconButton, Divider, Collapse, Checkbox } from "@mui/material";
import { useEffect, useState } from "react";
import { Connector } from "../model/Connector";
import { Route } from "../model/Route";
import EvConnectorsDropdown from "./EvConnectorsDropdown";
import { Point } from "../model/Point";
import { RouteOptions } from "../model/RouteOptions";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const DEFAULT_CAR_RANGE = 250;
const MAX_WIDTH = 345;

const START_POINT_HINT = "Введите координаты точки А";
const END_POINT_HINT = "Введите координаты точки Б";

const formatPoint = (point: Point | undefined) => {
  if (!point) {
    return "";
  }

  return `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`;
};

const parsePoint = (value: string): Point | undefined => {
  const [latValue, lngValue, ...rest] = value.split(",").map(part => part.trim());

  if (!latValue || !lngValue || rest.length > 0) {
    return undefined;
  }

  const lat = Number(latValue);
  const lng = Number(lngValue);

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return undefined;
  }

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return undefined;
  }

  return { lat, lng };
};

const isPointInputInvalid = (value: string) => value.trim() !== "" && !parsePoint(value);

export const ControlPanel = (
  props: {
    expanded: boolean,
    route: Route | undefined,
    startPoint: Point | undefined,
    endPoint: Point | undefined,
    waypoints: Point[],
    onStartPointChange: (point: Point | undefined) => void,
    onEndPointChange: (point: Point | undefined) => void,
    isLoading: boolean,
    onLoadRouteClick: (carRange: number, connectors: Connector[], options: RouteOptions) => void,
    onSwapStartEndPointsClick: () => void,
    onDeleteWaypointClick: (point: Point) => void,
    onResetClick: () => void,
    onExpandClick: () => void,
    onShowOptionalStationsChange: (show: boolean) => void,
  }
) => {

  const [startPointText, setStartPointText] = useState<string>(formatPoint(props.startPoint));
  useEffect(() => {
    setStartPointText(formatPoint(props.startPoint));
  }, [props.startPoint]);

  const [endPointText, setEndPointText] = useState<string>(formatPoint(props.endPoint));
  useEffect(() => {
    setEndPointText(formatPoint(props.endPoint));
  }, [props.endPoint]);

  const [carRange, setCarRange] = useState<number>(DEFAULT_CAR_RANGE);
  const [selectedConnectors, setSelectedConnectors] = useState<Connector[]>([]);
  const [showOptionalStations, setShowOptionalStations] = useState<boolean>(false);
  const [preferRightSideStations, setPreferRightSideStations] = useState<boolean>(false);
  const [disablePowerScoring] = useState<boolean>(false);
  const [disableDetourScoring] = useState<boolean>(false);

  const [chargesCount, setChargesCount] = useState<number | undefined>();

  const handleConnectorsChanged = (connectors: Connector[]) => {
    setSelectedConnectors(connectors);
  };

  const handleCarRangeChange = (range: number) => {
    if (range <= 0) return;

    setCarRange(range);
  };

  const handleShowOptionalStationsChange = (show: boolean) => {
    setShowOptionalStations(show);
    props.onShowOptionalStationsChange(show);
  };

  const handlePreferRightSideStationsChange = (value: boolean) => {
    setPreferRightSideStations(value);
  };

  const internalHandleReset = () => {
    setSelectedConnectors([]);
    setChargesCount(undefined);
    setCarRange(DEFAULT_CAR_RANGE);

    props.onResetClick();
  };

  const internalHandleLoadRoute = () => {
    props.onLoadRouteClick(carRange, selectedConnectors, {
      preferRightSideStations,
      disablePowerScoring,
      disableDetourScoring,
    })
  };

  const handleStartPointTextChange = (value: string) => {
    setStartPointText(value);

    if (value.trim() === "") {
      props.onStartPointChange(undefined);
      return;
    }

    const point = parsePoint(value);
    if (point) {
      props.onStartPointChange(point);
    }
  };

  const handleEndPointTextChange = (value: string) => {
    setEndPointText(value);

    if (value.trim() === "") {
      props.onEndPointChange(undefined);
      return;
    }

    const point = parsePoint(value);
    if (point) {
      props.onEndPointChange(point);
    }
  };

  return (
    <>
      <Box
        width={props.expanded ? MAX_WIDTH : undefined}
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 10,
          backgroundColor: 'white',
          padding: '10px',
          borderRadius: '8px',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px', // Расстояние между текстовыми полями
        }}
      >
        <Button
          variant="text"
          onClick={props.onExpandClick}
        >
          <Typography>{props.expanded ? 'Свернуть' : 'Развернуть'}</Typography>
          {props.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
        <Collapse in={props.expanded} timeout="auto" >
          <Box
            display="flex"
            alignItems="center"
            gap={2} // Отступ между блоком TextField'ов и иконкой
            mb={2} // Отступ снизу, если это часть списка
          >
            <Box display="flex" flexDirection="column" gap={1} flexGrow={1}>
              <TextField
                value={startPointText}
                label="Точка А"
                placeholder={START_POINT_HINT}
                onChange={(e) => handleStartPointTextChange(e.target.value)}
                error={isPointInputInvalid(startPointText)}
                helperText={isPointInputInvalid(startPointText) ? "Введите координаты в формате latitude, longitude" : "Формат: latitude, longitude"}
              />

              {
                props.waypoints.map(waypoint => (
                  <Box key={`${waypoint.lat}-${waypoint.lng}`} display="flex" alignItems="center" gap={1} mb={1}>
                    <TextField
                      value={`${waypoint.lat.toFixed(4)}, ${waypoint.lng.toFixed(4)}`}
                      slotProps={{
                        htmlInput: {
                          readOnly: true
                        }
                      }}
                    />
                    <IconButton aria-label="удалить" onClick={() => props.onDeleteWaypointClick(waypoint)}>
                      <Close />
                    </IconButton>
                  </Box>
                ))
              }
              <TextField
                value={endPointText}
                label="Точка Б"
                placeholder={END_POINT_HINT}
                onChange={(e) => handleEndPointTextChange(e.target.value)}
                error={isPointInputInvalid(endPointText)}
                helperText={isPointInputInvalid(endPointText) ? "Введите координаты в формате latitude, longitude" : "Формат: latitude, longitude"}
              />
            </Box>
            {
              props.waypoints.length === 0 && (
                <IconButton
                  disabled={!props.startPoint || !props.endPoint}
                  aria-label="поменять местами"
                  onClick={() => props.onSwapStartEndPointsClick()}>
                  <CompareArrows sx={{ transform: 'rotate(90deg)' }} />
                </IconButton>
              )
            }
          </Box>
          <Box display={'flex'} alignItems={'center'} mb={1}>
            <Checkbox checked={preferRightSideStations} onChange={(_, c) => {
              handlePreferRightSideStationsChange(c);
            }} />
            <Typography>Предпочитать станции справа</Typography>
          </Box>
          {/* <Box display={'flex'} alignItems={'center'} mb={1}>
            <Checkbox checked={disablePowerScoring} onChange={(_, c) => setDisablePower(c)} />
            <Typography>disable power scoring</Typography>
          </Box>
          <Box display={'flex'} alignItems={'center'} mb={2}>
            <Checkbox checked={disableDetourScoring} onChange={(_, c) => setDisableDetourScoring(c)} />
            <Typography>disable detour scoring</Typography>
          </Box> */}

          <Divider />

          <Box mt={1}>
            <Typography sx={{ mb: 1 }} fontWeight={'700'}>Информация об авто:</Typography>
            <EvConnectorsDropdown
              initialSelectedConnectors={selectedConnectors.map(c => c.name)}
              onConnectorsChange={handleConnectorsChanged} />
            <TextField
              fullWidth
              value={carRange}
              onChange={e => { handleCarRangeChange(Number(e.target.value)) }}
              label={'Дальность хода в км.'}
              type='number'
              margin='normal'
            />
          </Box>


          {props.route && (
            <>
              <Divider />
              <Box mt={1}>
                <Typography fontWeight={'700'}>Информация о маршруте:</Typography>
                <Box display={'flex'}>
                  <Typography>Показать все станции маршрута</Typography>
                  <Checkbox checked={showOptionalStations} onChange={(v, c) => {
                    handleShowOptionalStationsChange(c);
                  }} />
                </Box>
                {
                  props.route && (
                    <Box gap={1}>
                      <RouteOutlined />
                      {
                        props.route.reachable && props.route.segments.map(s => {
                          return <Typography>{`#${s.id} - ${s.length} км. - ${s.mandatoryPlaceSide || 'unknown'}`}</Typography>
                        })
                      }
                      {
                        !props.route.reachable && (
                          <Typography>{props.route.reason}</Typography>
                        )
                      }
                    </Box>
                  )
                }

                {
                  chargesCount &&
                  <Box display={'flex'} gap={1}>
                    <EvStationOutlined />
                    <Typography>
                      Требуется подзарядок: {chargesCount}
                    </Typography>
                  </Box>
                }
              </Box>
            </>
          )}

          <Box sx={{
            display: 'flex',
            justifyContent: 'space-around',
            mt: 3
          }}>
            <Button
              onClick={internalHandleReset}
              disabled={props.startPoint === undefined}
              color="error">
              Очистить
            </Button>
            <LoadingButton
              onClick={internalHandleLoadRoute}
              variant="contained"
              loading={props.isLoading}
              disabled={!props.startPoint || !props.endPoint}
            >
              Построить маршрут
            </LoadingButton>
          </Box>
        </Collapse>
      </Box>
    </>
  );
};
